import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, Film } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import PhotographyGuide from "@/components/sections/PhotographyGuide";
import { cdn, srcSet, thumb } from "@/lib/img";
import { ytThumbnail } from "@/lib/youtube";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HARDCODED_PHOTOS = [
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/z88nvx3c_IMG_7573.jpeg", label: "Rocky shoreline at Whale Cove on the Bay of Fundy", category: "Waterfront" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/0zj0zjrb_IMG_7577.jpeg", label: "Digby Neck coastline, Bay of Fundy — Nova Scotia", category: "Waterfront" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/xkvkomv6_IMG_7542%20%281%29.jpeg", label: "Misty morning on the Bay of Fundy rocks at Whale Cove", category: "Waterfront" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/fjjgmrzd_IMG_8812.jpeg", label: "Fog rolling over the Bay of Fundy shoreline at Whale Cove", category: "Waterfront" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/1iyby5ze_IMG_8792.jpeg", label: "Building the access road into the Whale Cove off-grid sites", category: "Campsite" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/shg7ofp3_IMG_8836.jpeg", label: "Moss-covered rocks along the Whale Cove forest trail to the Bay", category: "Trails" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/6snqon67_IMG_8834.jpeg", label: "Rock crevice on the trail down to the Bay of Fundy", category: "Trails" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/a4iykfzj_IMG_6060.jpeg", label: "Where the road meets the forest at Whale Cove — Digby Neck NS", category: "Campsite" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/sfsi5yb6_IMG_8932.JPG", label: "New gravel road through the birch — Whale Cove off-grid build", category: "Campsite" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/fz2wwh16_IMG_8933.JPG", label: "The access road, built on bedrock — Whale Cove Off Grid", category: "Campsite" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/0t4mbdem_IMG_8959.jpeg", label: "Quartz veins in the Bay of Fundy shoreline rock at Whale Cove", category: "Waterfront" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/xxz8bnzh_IMG_8964.jpeg", label: "Seaweed-covered boulder at low tide, Bay of Fundy — Whale Cove", category: "Waterfront" },
];

const CATEGORIES = ["All", "Waterfront", "Trails", "Campsite", "Wildlife", "Night Sky", "Tides"];

const PLACEHOLDERS = [];

export default function GalleryPage() {
  const [filter, setFilter] = useState("All");
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [videos, setVideos] = useState([]);
  const [videoLightbox, setVideoLightbox] = useState(null);

  const fetchUploaded = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/photos/public`);
      setUploadedPhotos(res.data.photos || []);
    } catch { /* ignore */ }
  }, []);

  const fetchVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/videos/public`);
      setVideos(res.data.videos || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchUploaded(); fetchVideos(); }, [fetchUploaded, fetchVideos]);

  // Merge hardcoded + uploaded photos
  const allPhotos = [
    ...HARDCODED_PHOTOS.map(p => ({ ...p, type: "hardcoded" })),
    ...uploadedPhotos.map(p => ({
      src: `${API}/photos/${p.id}/image`,
      label: p.label || p.original_filename,
      category: p.section === "gallery" ? "Campsite" : p.section?.charAt(0).toUpperCase() + p.section?.slice(1),
      type: "uploaded",
    })),
  ];

  const filteredPhotos = filter === "All" ? allPhotos : allPhotos.filter(p => p.category === filter);
  const filteredPlaceholders = filter === "All" ? PLACEHOLDERS : PLACEHOLDERS.filter(p => p.category === filter);

  return (
    <>
      {/* Header */}
      <div className="pt-24 pb-8 px-4 sm:px-8 bg-wc-cream">
        <div className="max-w-6xl mx-auto">
          <p className="font-accent italic text-wc-teal text-base mb-3">Photography</p>
          <h1 className="font-display text-wc-forest text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            The Bay of Fundy, photographed.
          </h1>
          <p className="font-body text-wc-slate text-base max-w-2xl">
            Real photos from Whale Cove Off Grid on Digby Neck, Nova Scotia — the shoreline, the trails, the campsite, and everything in between. More coming as we build out 2026.
          </p>
        </div>
      </div>

      {/* Photography guide (sidebar) + Photo grid */}
      <div className="wc-section bg-white pt-8" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] xl:grid-cols-[360px_1fr] gap-6 lg:gap-8">

            {/* Sidebar — Photography Guide */}
            <aside className="lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto scrollbar-none">
              <PhotographyGuide compact />
            </aside>

            {/* Photo masonry */}
            <div>
              {videos.length > 0 && (
                <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
                  <p className="font-body text-wc-slate text-xs uppercase tracking-[0.25em]">
                    {videos.length} {videos.length === 1 ? "video" : "videos"} · scroll to explore
                  </p>
                  <Link to="/videos" className="font-body text-wc-teal text-xs hover:text-wc-forest transition-colors inline-flex items-center gap-1">
                    <Film className="w-3.5 h-3.5" /> See all videos →
                  </Link>
                </div>
              )}
              <div className="gallery-masonry">
                {/* Videos first (featured gets pride of place) */}
                {videos.map((v, i) => (
                  <motion.div
                    key={`video-${v.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="group relative overflow-hidden rounded-sm cursor-pointer"
                    onClick={() => setVideoLightbox(v)}
                    data-testid={`gallery-video-${v.id}`}
                  >
                    <div className="aspect-video relative bg-black">
                      <img
                        src={v.custom_thumbnail_url ? cdn(v.custom_thumbnail_url, 700) : ytThumbnail(v.youtube_id, "maxresdefault")}
                        alt={v.title}
                        loading="lazy" decoding="async"
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        onError={(e) => {
                          if (!e.currentTarget.dataset.fallback) {
                            e.currentTarget.dataset.fallback = "1";
                            e.currentTarget.src = ytThumbnail(v.youtube_id, "hqdefault");
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30 group-hover:to-black/50 transition-colors" />
                      {/* Play badge */}
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 bg-black/60 text-white text-[10px] rounded font-body uppercase tracking-widest backdrop-blur-sm">
                        <Film className="w-3 h-3" /> Video
                      </span>
                      {/* Center play button */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="flex items-center justify-center w-14 h-14 rounded-full bg-wc-forest/90 border border-wc-cream/30 group-hover:bg-wc-teal group-hover:scale-110 transition-all duration-300">
                          <Play className="w-5 h-5 text-wc-cream translate-x-0.5" fill="currentColor" />
                        </span>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="font-accent italic text-white text-sm drop-shadow">{v.title}</p>
                    </div>
                  </motion.div>
                ))}

                {/* Photos */}
                {allPhotos.map((photo, i) => (
                  <motion.div
                    key={`photo-${i}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="group relative overflow-hidden rounded-sm cursor-pointer"
                    onClick={() => setLightbox(photo)}
                  >
                    <img
                      src={thumb(photo.src, 600)}
                      srcSet={srcSet(photo.src)}
                      sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                      alt={photo.label}
                      className="w-full object-cover wc-img group-hover:scale-[1.03] transition-transform duration-500"
                      style={{ aspectRatio: i % 3 === 0 ? "3/4" : i % 3 === 1 ? "4/3" : "1/1" }}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-wc-slate/0 group-hover:bg-wc-slate/20 transition-colors duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="font-accent italic text-white text-xs">{photo.label}</span>
                    </div>
                  </motion.div>
                ))}

                {PLACEHOLDERS.map((p, i) => (
                  <div
                    key={`ph-${i}`}
                    className="wc-image-placeholder rounded-sm"
                    style={{ aspectRatio: i % 2 === 0 ? "4/3" : "3/4" }}
                  >
                    <div className="text-center">
                      <p className="text-sm">{p.label}</p>
                      <span className="text-xs opacity-60 mt-1 block">{p.category}</span>
                    </div>
                  </div>
                ))}
              </div>

              {allPhotos.length === 0 && PLACEHOLDERS.length === 0 && (
                <p className="text-center font-body text-wc-slate/50 text-sm py-12">No photos in this category yet.</p>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl font-light z-10"
            onClick={() => setLightbox(null)}
          >
            &times;
          </button>
          <div className="relative max-w-full max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            {/* Low-fidelity placeholder (small, already cached from the grid) — shown instantly,
                blurred while the full image streams in. */}
            <img
              src={thumb(lightbox.src, 80)}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-contain rounded-sm scale-105 blur-2xl opacity-90"
            />
            {/* Full-resolution image — responsive so 1080p screens get ~900px wide,
                4K screens get ~1280px wide. */}
            <img
              src={cdn(lightbox.src, 1100, 60)}
              srcSet={`${cdn(lightbox.src, 800, 60)} 800w, ${cdn(lightbox.src, 1100, 60)} 1100w, ${cdn(lightbox.src, 1400, 60)} 1400w`}
              sizes="(max-width: 768px) 95vw, 80vw"
              alt={lightbox.label}
              decoding="async"
              fetchpriority="high"
              className="relative max-w-full max-h-[85vh] object-contain rounded-sm"
            />
          </div>
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 font-accent italic text-white/70 text-sm">
            {lightbox.label}
          </p>
        </div>
      )}

      {/* Video lightbox */}
      {videoLightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setVideoLightbox(null)}
          data-testid="gallery-video-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl font-light z-10"
            onClick={() => setVideoLightbox(null)}
          >
            &times;
          </button>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video rounded-sm overflow-hidden bg-black">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${videoLightbox.youtube_id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                title={videoLightbox.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
              />
            </div>
            <p className="font-display text-white text-lg mt-4">{videoLightbox.title}</p>
            {videoLightbox.description && (
              <p className="font-body text-white/70 text-sm mt-1 max-w-3xl">{videoLightbox.description}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
